import React, { useEffect, useState } from "react";
import { Bookmark, BookmarkX, ShieldOff, Plus, Trash2 } from "lucide-react";
import { enrichListSources, enrichUpsertSource, enrichDeleteSource } from "../../lib/adminApi";

const TRUST_TONE = {
  whitelisted: "text-green-400 border-green-700/40",
  downgraded: "text-amber-400 border-amber-600/40",
  blacklisted: "text-red-400 border-red-600/40",
};

const SOURCE_TYPES = ["official_brand", "distillery", "official_importer", "editorial", "competition", "retailer", "unknown"];

export default function AdminSources() {
  const [items, setItems] = useState([]);
  const [domain, setDomain] = useState("");
  const [trust, setTrust] = useState("whitelisted");
  const [type, setType] = useState("editorial");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => enrichListSources().then((d) => setItems(d.sources));
  useEffect(() => { load(); }, []);

  const upsert = async () => {
    if (!domain.trim()) return;
    setBusy(true);
    try {
      await enrichUpsertSource(domain.trim(), { domain: domain.trim(), trust, source_type: type, notes });
      setDomain(""); setNotes("");
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const remove = async (d) => {
    if (!confirm(`Remove ${d} from registry?`)) return;
    await enrichDeleteSource(d);
    load();
  };

  return (
    <div className="px-5 md:px-10 py-8 max-w-5xl" data-testid="admin-sources">
      <header className="mb-7">
        <p className="overline text-[#C17F59] mb-2 flex items-center gap-2"><Bookmark size={12} /> Source Registry</p>
        <h1 className="font-display text-4xl">Which sources do we trust?</h1>
        <p className="font-hand text-xl text-[#D89A74] -rotate-1 mt-1">— the bartender's bibliography.</p>
        <p className="text-sm text-white/55 mt-3 max-w-2xl">
          Whitelist official brand sites and distilleries. Downgrade lazy retailers and Wikipedia.
          Blacklist anything misleading. The Scout reads this every time it runs.
        </p>
      </header>

      <section className="bg-[#15171A] border border-white/8 p-5 mb-8" data-testid="source-upsert">
        <p className="overline text-[#C17F59] mb-3 flex items-center gap-2"><Plus size={12} /> Add or update a domain</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <label className="block md:col-span-2">
            <span className="overline text-white/50 mb-1 block">Domain</span>
            <input value={domain} onChange={(e) => setDomain(e.target.value)}
                   placeholder="e.g. hapusagin.com"
                   className="w-full bg-transparent border-b border-white/15 focus:border-[#C17F59] py-2 text-sm focus:outline-none"
                   data-testid="source-domain" />
          </label>
          <label className="block">
            <span className="overline text-white/50 mb-1 block">Trust</span>
            <select value={trust} onChange={(e) => setTrust(e.target.value)}
                    className="w-full bg-transparent border-b border-white/15 focus:border-[#C17F59] py-2 text-sm focus:outline-none"
                    data-testid="source-trust">
              <option value="whitelisted">Whitelisted</option>
              <option value="downgraded">Downgraded</option>
              <option value="blacklisted">Blacklisted</option>
            </select>
          </label>
          <label className="block">
            <span className="overline text-white/50 mb-1 block">Source type</span>
            <select value={type} onChange={(e) => setType(e.target.value)}
                    className="w-full bg-transparent border-b border-white/15 focus:border-[#C17F59] py-2 text-sm focus:outline-none"
                    data-testid="source-type">
              {SOURCE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
        </div>
        <label className="block mt-3">
          <span className="overline text-white/50 mb-1 block">Notes (optional)</span>
          <input value={notes} onChange={(e) => setNotes(e.target.value)}
                 className="w-full bg-transparent border-b border-white/15 focus:border-[#C17F59] py-2 text-sm focus:outline-none"
                 data-testid="source-notes" />
        </label>
        <button onClick={upsert} disabled={busy || !domain.trim()}
                className="mt-4 inline-flex items-center gap-2 bg-[#C17F59] text-[#0E0F0C] px-5 py-2 text-xs uppercase tracking-[0.25em] hover:bg-[#D89A74] disabled:opacity-50"
                data-testid="source-save">
          <Plus size={12} /> {busy ? "Saving..." : "Save source"}
        </button>
      </section>

      <section className="bg-[#15171A] border border-white/8" data-testid="source-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/8 text-left">
              <th className="py-3 px-4 overline text-white/40">Domain</th>
              <th className="py-3 px-4 overline text-white/40">Trust</th>
              <th className="py-3 px-4 overline text-white/40">Type</th>
              <th className="py-3 px-4 overline text-white/40">Notes</th>
              <th className="py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.domain} className="border-b border-white/4" data-testid={`source-row-${s.domain}`}>
                <td className="py-3 px-4 font-display text-base">{s.domain}</td>
                <td className="py-3 px-4">
                  <span className={`inline-block text-[10px] uppercase tracking-[0.22em] border px-2 py-0.5 ${TRUST_TONE[s.trust] || ""}`}>
                    {s.trust === "whitelisted" && <Bookmark size={9} className="inline mr-1" />}
                    {s.trust === "downgraded" && <BookmarkX size={9} className="inline mr-1" />}
                    {s.trust === "blacklisted" && <ShieldOff size={9} className="inline mr-1" />}
                    {s.trust}
                  </span>
                </td>
                <td className="py-3 px-4 text-white/70">{s.source_type}</td>
                <td className="py-3 px-4 text-white/55 text-xs italic">{s.notes || "—"}</td>
                <td className="py-3 px-4 text-right">
                  <button onClick={() => remove(s.domain)}
                          className="inline-flex items-center gap-1 text-xs text-white/40 hover:text-red-400"
                          data-testid={`source-delete-${s.domain}`}>
                    <Trash2 size={11} />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="5" className="text-white/40 text-sm p-6 text-center">No sources yet.</td></tr>}
          </tbody>
        </table>
      </section>
    </div>
  );
}
